<template>
  <div class="rd-screen">
    <report-tools :copy="companies.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="loading && !rows.length" kind="loading"></report-state>
    <report-state v-else-if="!companies.length" icon="mdi-truck-outline" :title="emptyTitle" :text="emptyText"></report-state>

    <ui-table
      v-else
      variant="report"
      :columns="columns"
      :rows="companies"
      :row-key="(company) => company.key"
      :totals="footTotals"
      :total-label="$t('total')"
    >
      <template #cell-num="{ index }">{{ index + 1 }}</template>

      <template #cell-shift="{ row, column }">
        <span
          v-if="cell(row, column.shift).ordered"
          class="rd-cellbar"
          :class="{ 'is-complete': cell(row, column.shift).complete }"
        >
          <span class="rd-cellbar__text">
            {{ cell(row, column.shift).taken }} / {{ cell(row, column.shift).ordered }}
          </span>
          <span class="rd-cellbar__bar"><i :style="{ width: `${cell(row, column.shift).percent}%` }"></i></span>
        </span>
        <span v-else class="is-zero">–</span>
      </template>

      <template #cell-total="{ row }">
        <b>{{ row.taken }}</b> / {{ row.ordered }}
        <span class="rd-mini" :class="{ 'is-complete': row.complete }">
          <i :style="{ width: `${row.percent}%` }"></i>
        </span>
      </template>
    </ui-table>
  </div>
</template>

<script>
import { SHIFTS } from "@/constants/shifts";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import { parseTaken, percentOf } from "@/components/report/shared/takenMath";

/**
 * Preuzeto (gosti): firma × smena (preuzeto / poručeno sa trakom napretka) i ukupno.
 * Ne učitava ništa; podatke daje ekran koji ga koristi (stari tab sa nizom { menu, shift_one…, total }).
 */
export default {
  name: "GuestTakenView",
  components: { ReportState, ReportTools },
  props: {
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    search: { type: String, default: "" },
  },
  computed: {
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      return [
        { key: "num", label: "#", headCls: "rd-col-num", num: true, zero: () => true },
        { key: "name", label: this.$t("company"), name: true },
        ...this.visibleShifts.map((shift) => ({ key: shift.key, label: this.$t(shift.label), slot: "shift", shift, align: "end" })),
        { key: "total", label: this.$t("total"), align: "end", num: true },
      ];
    },
    /** Zbirni red (tekst): preuzeto / poručeno po smeni i ukupno. */
    footTotals() {
      const totals = { total: `${this.summary.taken} / ${this.summary.ordered}` };
      this.visibleShifts.forEach((shift) => {
        const sum = this.shiftTotal(shift);
        totals[shift.key] = sum.ordered ? `${sum.taken} / ${sum.ordered}` : "–";
      });
      return totals;
    },
    companies() {
      const searched = this.customSearch(
        this.rows.map((item, index) => ({ ...item, key: index, menu: this.toLatin(item.menu || "") })),
        [{ value: "menu" }],
        this.search
      );
      return searched
        .map((item) => {
          const total = parseTaken(item.total);
          return {
            key: item.key,
            source: item,
            name: item.menu,
            taken: total.taken,
            ordered: total.ordered,
            complete: total.ordered > 0 && total.taken >= total.ordered,
            percent: percentOf(total.taken, total.ordered),
          };
        })
        .filter((company) => company.ordered > 0 || company.taken > 0);
    },
    /** Smene 1–3 uvek, četvrta samo kad neka firma ima obroke u njoj. */
    visibleShifts() {
      return SHIFTS.filter((shift) => shift.id < 4 || this.shiftTotal(shift).ordered > 0);
    },
    summary() {
      const taken = this.companies.reduce((sum, company) => sum + company.taken, 0);
      const ordered = this.companies.reduce((sum, company) => sum + company.ordered, 0);
      return { taken, ordered, percent: percentOf(taken, ordered) };
    },
    emptyTitle() {
      return this.search
        ? this.$t("report_mobile.no_results_for", { search: this.search })
        : this.$t("no_taken_meals_data");
    },
    emptyText() {
      return this.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        {
          key: "taken",
          label: this.$t("taken"),
          value: this.summary.taken,
          of: this.summary.ordered,
          percent: this.summary.percent,
          tone: "inverse",
        },
        { key: "companies", label: this.$t("companies"), value: this.companies.length },
        {
          key: "left",
          label: this.$t("report_desktop.remaining"),
          value: Math.max(0, this.summary.ordered - this.summary.taken),
          tone: "accent",
        },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.companies.length ? kpis : []);
      },
      immediate: true,
    },
  },
  methods: {
    cell(company, shift) {
      const { taken, ordered } = parseTaken(company.source[shift.key]);
      return { taken, ordered, percent: percentOf(taken, ordered), complete: ordered > 0 && taken >= ordered };
    },
    shiftTotal(shift) {
      return this.companies.reduce(
        (total, company) => {
          const { taken, ordered } = parseTaken(company.source[shift.key]);
          return { taken: total.taken + taken, ordered: total.ordered + ordered };
        },
        { taken: 0, ordered: 0 }
      );
    },
    async copyTable() {
      const headers = [this.$t("company"), ...this.visibleShifts.map((shift) => this.$t(shift.label)), this.$t("total")];
      const rows = this.companies.map((company) => [
        company.name,
        ...this.visibleShifts.map((shift) => {
          const { taken, ordered } = parseTaken(company.source[shift.key]);
          return ordered ? `${taken}/${ordered}` : "";
        }),
        `${company.taken}/${company.ordered}`,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
